import { ChangeDetectorRef, Directive, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import isUndefined from 'lodash-es/isUndefined';
import pickBy from 'lodash-es/pickBy';
import { Subscription } from 'rxjs';
import { Catalog } from '@df/catalog/catalog.interfaces';
import { Store } from '@df/catalog/store/store';
import { StoreResource } from '@df/catalog/store/store.resource';
import { StoreService } from '@df/catalog/store/store.service';
import { App } from '@df/core/app';
import { CheckoutFormComponentDef } from '@df/checkout/checkout-form.component.def';
import { CheckoutStepComponentDef } from '@df/checkout/checkout-step.component.def';
import { Status } from '@df/core/status';
import { SessionStorage } from '@df/browser/storage/decorator/session-storage';

export interface IGeolocationCoordinates {
    readonly accuracy: number;
    readonly altitude: number | null;
    readonly altitudeAccuracy: number | null;
    readonly heading: number | null;
    readonly latitude: number;
    readonly longitude: number;
    readonly speed: number | null;
}

@Directive({})
export class CheckoutStorePickupComponentDef implements OnDestroy, OnInit {
    @Input()
    maxDistance?: number;

    @Input()
    forceCountry?: string;

    @Input()
    allMode?: boolean;

    stores: Store[] = [];

    loadStatus = new Status();

    geoStatus = new Status();

    @SessionStorage('checkoutStorePickupLocation')
    location?: string;

    geolocationResult = false;

    protected subscriptions = new Subscription();

    /**
     * Step form  (check also ngOnDestroy)
     * important: you need to put #stepForm="ngForm" on form element in template
     */
    protected _stepForm?: NgForm;

    @ViewChild('stepForm', { static: false })
    set stepForm(form: NgForm) {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.checkoutStepComponent.addForm(form);
        this._stepForm = form;
    }

    constructor(
        protected checkoutFormComponent: CheckoutFormComponentDef,
        protected checkoutStepComponent: CheckoutStepComponentDef,
        protected changeDetectorRef: ChangeDetectorRef,
        protected storeService: StoreService,
        protected app: App,
        protected storeResource: StoreResource
    ) {}

    ngOnInit() {
        [this.loadStatus, this.saveStatus, this.geoStatus, this.checkoutStepComponent.submittedSubject].forEach(i =>
            this.subscriptions.add(i.subscribe(() => this.detectChanges()))
        );
        this.onInitLoad();
    }

    protected onInitLoad(): void {
        this.load();
    }

    protected load() {
        if (this.allMode) {
            this.loadAll();
        } else {
            this.searchStores();
        }
    }

    ngOnDestroy() {
        this.checkoutStepComponent.removeForm(this._stepForm);
        this.subscriptions.unsubscribe();
    }

    get currentStoreId(): number | undefined {
        return this.checkoutFormComponent.selfCollectStoreId;
    }

    set currentStoreId(id: number | undefined) {
        this.checkoutFormComponent.selfCollectStoreId = id;
    }

    get currentStore(): Store | undefined {
        if (this.currentStoreId) {
            return this.app.resources.store.getExisting(this.currentStoreId);
        }
        return undefined;
    }

    get saveStatus(): Status {
        return this.checkoutFormComponent.saveShippingMethodStatus;
    }

    trackByStoreId(index: number, store: Store): number {
        return store.id;
    }

    searchStores() {
        if (this.location && !this.geoStatus.busy) {
            this.geoStatus.reset();
            const data = this.getSearchData(this.location);
            return this.storeService.locate(this.loadStatus, data, (stores: Store[]) => {
                this.geolocationResult = false;
                this.onStoresLoad(stores);
            });
        }
    }

    protected onStoresLoad(stores: Store[]): void {
        this.stores = stores.filter(store => store.data?.allow_for_checkout);
        if (this.currentStoreId && !this.stores.find(store => store.data?.id === this.currentStoreId)) {
            this.currentStoreId = undefined;
        }
        this.changeDetectorRef.detectChanges();
    }

    protected loadAll(verbosity = 1): void {
        this.storeService.loadAll(this.loadStatus, verbosity, () => this.onStoresLoad(this.storeResource.getAllExisting()));
    }

    protected detectChanges() {
        this.changeDetectorRef.detectChanges();
    }

    private getSearchData(location: string): Catalog.IFindableLocateOptions {
        return {
            ...this.getDefaultOptions(),
            address: location
        };
    }

    private getDefaultOptions(): { limit?: number; maxDistance?: number } {
        return pickBy(
            {
                country: this.forceCountry,
                maxDistance: this.maxDistance
            },
            value => !isUndefined(value)
        );
    }

    submit(): void {
        this.app.request.submitForm(this._stepForm, undefined, () => {
            return this.searchStores();
        });
    }

    /////////////////////////////////////////////////
    /////
    ///// Geolocation
    /////
    /////////////////////////////////////////////////

    searchByGeolocation(status = this.geoStatus): void {
        if (this.geolocationAvailable && !this.loadStatus.busy && !this.geoStatus.busy) {
            this.loadStatus.reset();
            this.app.request.send(
                status,
                () => {
                    return new Promise((resolve, reject) => {
                        this.getGeolocation()
                            .then((response: IGeolocationCoordinates) => {
                                this.storeService.locate(
                                    undefined,
                                    {
                                        ...this.getDefaultOptions(),
                                        latitude: response.latitude,
                                        longitude: response.longitude
                                    },
                                    (stores: Store[]) => {
                                        resolve({ data: stores });
                                    },
                                    reject
                                );
                            })
                            .catch(reject);
                    });
                },
                (stores: Store[]) => {
                    this.geolocationResult = true;
                    this.onStoresLoad(stores);
                }
            );
        }
    }

    /**
     * Checks if geolocation is available in the browser
     * @returns {boolean}
     */
    get geolocationAvailable(): boolean {
        if (navigator && navigator.geolocation && navigator.geolocation.getCurrentPosition) {
            return true;
        }
        return false;
    }

    /**
     * Returns geolocation from browser
     * @returns {IPromise<Coordinates>}
     */
    protected getGeolocation(): Promise<GeolocationCoordinates> {
        return new Promise((resolve, reject) => {
            window.navigator.geolocation.getCurrentPosition(
                position => {
                    resolve(position.coords);
                },
                () => {
                    // if not enabled we throw error
                    reject({ error: 'Please enable geolocation in your browser' });
                },
                {
                    timeout: 10000
                }
            );
        });
    }
}
